/**
 * `usePiiReveal` - state machine for one MaskedField.
 *
 * Responsibilities (per ADR-013 + AC-8/9):
 *
 *   - `state`: 'masked' | 'revealing' | 'revealed' | 'error'.
 *   - `reveal()`: POST `/audit/pii-revealed` (fail-closed), then enter
 *     'revealed' on 2xx; on non-2xx enter 'error' (field stays masked).
 *   - `mask()`: user-initiated re-mask; fires the disposal audit with
 *     `reason='user'`.
 *   - Auto-re-mask after 30s of inactivity on the field's container.
 *   - Auto-re-mask on `visibilitychange=hidden` or window blur.
 *   - Auto-re-mask on hook unmount (navigation away).
 *
 *   The re-mask audits are fire-and-forget; only the initial reveal is
 *   fail-closed. See `lib/audit.ts` for the asymmetry rationale.
 *
 * Auth/network errors (401/403) are surfaced to the caller as a `BffError`
 * via `onAuthError` so the page's `useBffError` provider can route them.
 *
 * The activity-watcher attaches to the field's `containerRef` (so the
 * 30-second idle timer resets on mouse / keyboard / touch activity
 * inside the revealed region, not on activity elsewhere on the page).
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import type { BffClient, PiiField, PiiResource } from '../../../lib/bff-client'
import { revealAudit, remaskAudit } from '../../../lib/audit'

/** State machine values. */
export type RevealState = 'masked' | 'revealing' | 'revealed' | 'error'

export interface UsePiiRevealArgs {
  readonly client: BffClient
  /** Resource id - account UUID or holder UUID; carried on the audit event. */
  readonly accountId: string
  readonly field: PiiField
  /**
   * Resource the reveal targets (SM2-7). Defaults to `account` so the
   * existing account MaskedFields are unchanged; the holder region passes
   * `holder` so the BFF emits `murmuration.holder.pii_revealed`.
   */
  readonly resource?: PiiResource | undefined
  /** Container whose activity (mouse / keyboard / touch) resets the idle timer. */
  readonly containerRef: React.RefObject<HTMLElement | null>
  /** Forwarded the 401/403 BffError so the page can route it. */
  readonly onAuthError?: ((err: unknown) => void) | undefined
}

export interface UsePiiRevealResult {
  readonly state: RevealState
  readonly correlationId: string | null
  /**
   * True for the last `FADE_LEAD_MS` of the idle window before an
   * auto-re-mask fires. Components use this to render a fade-out
   * affordance and / or surface a "field will hide in N seconds" toast.
   * Resets to false on activity or when the field is re-masked.
   */
  readonly fading: boolean
  readonly reveal: () => Promise<void>
  readonly mask: (reason?: RemaskReason) => void
  readonly clearError: () => void
}

/** Reasons the SPA emits for the auto-re-mask audit. */
export type RemaskReason = 'inactivity_30s' | 'tab_blur' | 'navigation' | 'user'

/** Idle timeout before auto-re-mask, per AC-9 (ADR-013). */
export const INACTIVITY_TIMEOUT_MS = 30_000

/**
 * Lead time before the auto-re-mask during which the field is in the
 * "fading" state. The field opacities down and the SPA surfaces a polite
 * toast so the user has a moment to interact and reset the idle timer
 * instead of being surprised by the re-mask. Per the MUR "Mockups" reveal states + the ADR-013 reveal interaction spec.
 */
export const FADE_LEAD_MS = 5_000

export function usePiiReveal({
  client,
  accountId,
  field,
  resource = 'account',
  containerRef,
  onAuthError,
}: UsePiiRevealArgs): UsePiiRevealResult {
  const [state, setState] = useState<RevealState>('masked')
  const [correlationId, setCorrelationId] = useState<string | null>(null)
  const [fading, setFading] = useState(false)
  const idleTimerRef = useRef<number | null>(null)
  const fadeTimerRef = useRef<number | null>(null)
  // `revealedRef` mirrors `state === 'revealed'` for use inside long-lived
  // listeners (where capturing the latest state via closure would race).
  const revealedRef = useRef(false)

  const clearIdleTimer = useCallback((): void => {
    if (idleTimerRef.current !== null) {
      window.clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }
    if (fadeTimerRef.current !== null) {
      window.clearTimeout(fadeTimerRef.current)
      fadeTimerRef.current = null
    }
  }, [])

  // `maskImpl` does not depend on the most recent state, so the timer /
  // listeners can call it without re-binding. It is the single point of
  // truth for entering the masked state and emitting the disposal audit.
  const maskImpl = useCallback(
    (reason: RemaskReason): void => {
      if (!revealedRef.current) {
        // Already masked - nothing to undo, no audit to emit.
        return
      }
      revealedRef.current = false
      clearIdleTimer()
      setFading(false)
      setState('masked')
      setCorrelationId(null)
      remaskAudit(client, accountId, field, reason, resource)
    },
    [client, accountId, field, resource, clearIdleTimer],
  )

  const armIdleTimer = useCallback((): void => {
    clearIdleTimer()
    setFading(false)
    fadeTimerRef.current = window.setTimeout(() => {
      setFading(true)
    }, INACTIVITY_TIMEOUT_MS - FADE_LEAD_MS)
    idleTimerRef.current = window.setTimeout(() => {
      maskImpl('inactivity_30s')
    }, INACTIVITY_TIMEOUT_MS)
  }, [clearIdleTimer, maskImpl])

  const reveal = useCallback(async (): Promise<void> => {
    if (state === 'revealing' || state === 'revealed') return
    setState('revealing')
    setCorrelationId(null)
    const result = await revealAudit(client, accountId, field, resource)
    if (result.ok) {
      revealedRef.current = true
      setCorrelationId(result.correlationId)
      setState('revealed')
      armIdleTimer()
      return
    }
    // Non-2xx: stay masked, surface the correlation id in `error` state.
    setCorrelationId(result.correlationId)
    setState('error')
    if (result.authError && onAuthError) {
      onAuthError(result.authError)
    }
  }, [client, accountId, field, resource, state, armIdleTimer, onAuthError])

  const mask = useCallback(
    (reason: RemaskReason = 'user'): void => {
      maskImpl(reason)
    },
    [maskImpl],
  )

  const clearError = useCallback((): void => {
    setState((current) => (current === 'error' ? 'masked' : current))
    setCorrelationId(null)
  }, [])

  // Activity watcher: any mouse / keyboard / touch event inside the
  // container resets the idle timer while the field is revealed. We do
  // NOT attach these globally - the spec is "no activity on the
  // containing region", not "no activity anywhere on the page".
  useEffect(() => {
    if (state !== 'revealed') return undefined
    const container = containerRef.current
    if (!container) return undefined
    const handler = (): void => {
      armIdleTimer()
    }
    container.addEventListener('mousemove', handler)
    container.addEventListener('mousedown', handler)
    container.addEventListener('keydown', handler)
    container.addEventListener('touchstart', handler)
    return () => {
      container.removeEventListener('mousemove', handler)
      container.removeEventListener('mousedown', handler)
      container.removeEventListener('keydown', handler)
      container.removeEventListener('touchstart', handler)
    }
  }, [state, containerRef, armIdleTimer])

  // Tab / window blur: re-mask immediately. `visibilitychange` covers
  // tab-switch and minimise; `blur` on window covers focus shifts to
  // other apps that do not change visibility (per AC-9).
  useEffect(() => {
    if (state !== 'revealed') return undefined
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') {
        maskImpl('tab_blur')
      }
    }
    const onWindowBlur = (): void => {
      maskImpl('tab_blur')
    }
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('blur', onWindowBlur)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('blur', onWindowBlur)
    }
  }, [state, maskImpl])

  // Unmount / navigation: re-mask if still revealed. Effect cleanup runs
  // on route change because the entire detail page tree unmounts.
  useEffect(() => {
    return () => {
      if (revealedRef.current) {
        remaskAudit(client, accountId, field, 'navigation', resource)
      }
      clearIdleTimer()
    }
    // We intentionally do NOT re-run this on every dep change - this
    // cleanup must fire only on unmount.
  }, [])

  return { state, correlationId, fading, reveal, mask, clearError }
}
